<template>
  <div>
    <v-client-table
      :data="table.items"
      :columns="table.columns"
      :options="table.options"
      class="table-default"
    >
      <template #h__full_name>
        <div class="table-default__left">Full name</div>
      </template>
      <template #h__group>
        <div class="table-default__left">Group</div>
      </template>
      <template #h__diet>
        <div class="table-default__left">Diet</div>
      </template>
      <template #count="{ index }">{{ index }}</template>
      <template #full_name="{ row }">
        <div class="table-default__left table-default__left--photo">
          <span
            :class="`fflag ff-md fflag-${row.client.citizenship ? row.client.citizenship.code : 'empty'}`"
            v-tooltip="{ content: row.client.citizenship ? row.client.citizenship.name : 'Not provided', placement: 'top-start', offset: 10 }"
          ></span>
          <template v-if="row.client.gender">
            <img v-if="row.client.gender.code === 'male'" src="@/assets/img/male-icon.svg" alt="">
            <img v-if="row.client.gender.code === 'female'" src="@/assets/img/female-icon.svg" alt="">
          </template>
          <img v-else src="@/assets/img/gender-plug-icon.svg" alt="">
          <router-link
            v-if="is_general_manager || is_sales_manager || is_operations_manager || is_operations_supervisor || is_sales_supervisor"
            :to="{ name: 'clients-detail', params: { id: row.client.id } }"
            class="table-default__link-border"
          >
            {{ row.client.full_name }}
          </router-link>
          <span v-else>{{ row.client.full_name }}</span>
          <span v-if="row.client.age">({{ row.client.age }} y.o.)</span>
          <img v-if="row.client.category && row.client.category.code === 'tour_leader'" src="@/assets/img/tour-leader-icon.svg" alt="Leader">
          <img v-if="row.client.birthday" src="@/assets/img/birthday-icon.svg" alt="Birthday">
          <img v-if="row.client.is_primary_contact" src="@/assets/img/bookmark-icon.svg" alt="Primary">
        </div>
      </template>
      <template #group="{ row }">
        <template v-if="row.client.group">
          <router-link
            v-if="is_general_manager || is_sales_manager || is_operations_manager || is_operations_supervisor || is_sales_supervisor"
            :to="{ name: 'clients-groups-general', params: { id: row.client.group.id } }"
            class="table-default__link-border"
          >
            {{ row.client.group.name }}
          </router-link>
          <div v-else class="table-default__left">{{ row.client.group.name }}</div>
        </template>
        <div v-else class="table-default__left">-</div>
      </template>
      <template #communication="{ row }">
        <div class="table-default__controls">
          <button
            v-tooltip="{content: row.client.communication.email, placement: 'top-start', offset: 10}"
            @click="onCopyContactLink(row.client.communication.email)"
            class="table-default__activity table-default__activity--mail"
            :class="{'table-default__activity--active': row.client.communication.email}"
          >
          </button>
          <button
            v-tooltip="{content: row.client.communication.phone, placement: 'top-start', offset: 10}"
            @click="onCopyContactLink(row.client.communication.phone)"
            class="table-default__activity table-default__activity--phone"
            :class="{'table-default__activity--active': row.client.communication.phone}"
          >
          </button>
          <a
            :href="`https://t.me/${row.client.communication.phone}`"
            target="_blank"
            class="table-default__activity table-default__activity--tg"
            :class="{'table-default__activity--active': row.client.communication.phone_telegram}"
          >
          </a>
          <a
            :href="`https://wa.me/${row.client.communication.phone}`"
            target="_blank"
            class="table-default__activity table-default__activity--wa"
            :class="{'table-default__activity--active': row.client.communication.phone_whatsapp}"
          >
          </a>
        </div>
      </template>
      <template #diet="{ row }">
        <div class="table-default__controls table-default__left">
          <span v-if="!row.client.food.food_preference">-</span>
          <template v-else>
            <span
              v-if="row.client.food.food_allergy"
              v-tooltip="{content: row.client.food.food_allergy, placement: 'top-start', offset: 10}"
              class="table-default__activity table-default__activity--active table-default__activity--food-allergy"
            ></span>
            <span v-if="row.client.food.food_gluten_free" class="table-default__activity table-default__activity--active table-default__activity--gf"></span>
            <span v-if="row.client.food.food_lactose_free" class="table-default__activity table-default__activity--active table-default__activity--lf"></span>
            <span>{{ row.client.food.food_preference.name }}</span>
          </template>
        </div>
      </template>
      <template #manager="{ row }">
        <div class="table-default__controls">
          <div class="table-default__photo">
            <img
              :src="getUserAvatar(row.client.manager.crop.crop_url)" alt=""
              v-tooltip="{ content: row.client.manager.full_name, placement: 'top-start', offset: 10, html: true }"
            >
          </div>
        </div>
      </template>
      <template #safari_insurance_is_filled="{ row }">
        <div class="table-default__controls">
          <span
            v-if="row.client.safari_insurance_is_filled"
            class="table-default__insurance"
            :class="{ 'table-default__insurance--safari': row.client.safari_insurance_is_filled }"
            v-tooltip="{content: tooltipContent(row.client.safari_insurance), placement: 'top-start', offset: 10, html: true}"
          ></span>
          <span
            v-else
            class="table-default__insurance"
            :class="{ 'table-default__insurance--safari': row.client.safari_insurance_is_filled }"
          ></span>
        </div>
      </template>
      <template #snack_bar="{ row }">
        <div class="table-default__controls">
          <a
            v-if="is_general_manager || is_sales_manager || is_sales_supervisor"
            :href="`${family_url}${row.client.hash}/snacks/${row.hash}`"
            target="_blank"
            v-tooltip="{content: tooltipSnackBarContent(row.snack_bar), placement: 'top-start', offset: 10, html: true}"
            class="table-default__activity table-default__activity--snacks table-default__activity--adds table-default__activity--active"
            :class="{
              'table-default__activity--snacks-time': !row.snack_bar.completed,
              'table-default__activity--snacks-no-need': row.snack_bar.no_need,
              'table-default__activity--snacks-usd': row.snack_bar.completed && row.snack_bar.outstanding_amount > 0,
              'table-default__activity--snacks-complete': row.snack_bar.completed && row.snack_bar.outstanding_amount === 0
            }"
          />
          <span
            v-else
            v-tooltip="{content: tooltipSnackBarContent(row.snack_bar), placement: 'top-start', offset: 10, html: true}"
            class="table-default__activity table-default__activity--snacks table-default__activity--adds table-default__activity--active"
            :class="{
              'table-default__activity--snacks-time': !row.snack_bar.completed,
              'table-default__activity--snacks-no-need': row.snack_bar.no_need,
              'table-default__activity--snacks-usd': row.snack_bar.completed && row.snack_bar.outstanding_amount > 0,
              'table-default__activity--snacks-complete': row.snack_bar.completed && row.snack_bar.outstanding_amount === 0
            }"
          />
        </div>
      </template>
      <template #controls="{ row }">
        <div class="table-default__controls">
          <button @click="onDeleteButtonClick(row)" type="button">
            <img src="@/assets/img/delete-table-icon.svg" alt="">
          </button>
        </div>
      </template>
    </v-client-table>
    <modal
      name="delete-detail"
      width="96%"
      height="auto"
      :max-width="480"
      :adaptive="true"
      :scrollable="true"
      @opened="focusDetailClose"
    >
      <div class="cancel-client-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-client-detail-title">
        <AppModalHeader ref="detailHeader" design="new" title="Confirm cancellation" title-id="delete-client-detail-title" @close="$modal.hide('delete-detail')" />
        <div v-if="current_client" class="cancel-client-dialog__body">
          <dl class="cancel-client-dialog__client">
            <div>
              <dt>Client</dt>
              <dd>{{ current_client.client.full_name }}</dd>
            </div>
          </dl>
          <section class="cancel-client-dialog__section" aria-labelledby="snack-bar-refund-title">
            <h3 id="snack-bar-refund-title">Snack bar refund</h3>
            <dl class="cancel-client-dialog__facts">
              <div>
                <dt>Order status</dt>
                <dd>
                  <template v-if="current_client.snack_bar && current_client.snack_bar.completed">
                    {{ current_client.snack_bar.no_need ? 'No need' : 'Completed' }} — {{ current_client.snack_bar.items_count }} item(s)
                  </template>
                  <template v-else>Not completed</template>
                </dd>
              </div>
              <div>
                <dt>Order amount</dt>
                <dd>{{ formatAmount(current_client.snack_bar && current_client.snack_bar.order_amount) }}</dd>
              </div>
              <div>
                <dt>Payment recorded</dt>
                <dd>{{ formatAmount(current_client.snack_bar && current_client.snack_bar.credit) }}</dd>
              </div>
              <div>
                <dt>Refund amount</dt>
                <dd>{{ formatAmount(snackbarRefundAmount) }}</dd>
              </div>
            </dl>
            <p
              class="cancel-client-dialog__refund-note"
              :class="{ 'cancel-client-dialog__refund-note--blocked': snackbarRefundAmount > 0 }"
            >
              <template v-if="snackbarRefundAmount > 0">
                This client cannot be removed because a Snack Bar payment is recorded. Contact support first.
              </template>
              <template v-else>
                No refund request will be created because no Snack bar payment is recorded.
              </template>
            </p>
          </section>
          <AppFormGroup design="new" label="Reason for removal" label-for="removal-reason">
            <AppTextarea id="removal-reason" design="new" v-model="removal_reason" placeholder="Optional" />
          </AppFormGroup>
          <app-cells position="center" :indent="false">
            <AppButton
              ref="confirmDelete"
              :disabled="deleting || snackbarRefundAmount > 0"
              :aria-busy="deleting"
              @click.native="deleteClient"
            >
              Confirm
            </AppButton>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { deleteClientSafariGroup, getSafariGroupsClientList } from '@/http'
import { format, parse } from 'date-fns'

export default {
  name: 'SafariGroupsDetailClients',
  props: {
    groupDetail: { Object }
  },
  data() {
    return {
      clients_options: [],
      table: {
        items: [],
        columns: ['count', 'full_name', 'group', 'manager', 'diet', 'safari_insurance_is_filled', 'snack_bar'],
        options: {
          headings: {
            count: '#',
            full_name: 'Full name',
            communication: 'Communication',
            group: 'Group',
            manager: 'Manager',
            diet: 'Diet',
            safari_insurance_is_filled: 'Insurance',
            snack_bar: 'Snack Bar',
            controls: '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 200,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      current_client: null,
      removal_reason: '',
      deleting: false,
      checked_all: false,
      checked: [],
      is_show_flying_block: false,
      family_url: process.env.VUE_APP_FAMILY,
    }
  },
  computed: {
    snackbarRefundAmount() {
      if (!this.current_client || !this.current_client.snack_bar) return 0
      return Number(this.current_client.snack_bar.credit) || 0
    },
    is_general_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('general_manager')
    },
    is_sales_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_manager')
    },
    is_sales_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('sales_supervisor')
    },
    is_operations_manager() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_manager')
    },
    is_operations_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('operations_supervisor')
    },
    is_safari_supervisor() {
      return this.$store.state.userGroups.map(item => item.code).includes('safari_supervisor')
    },
  },
  created() {
    if (this.is_general_manager || this.is_safari_supervisor || this.is_sales_supervisor || this.is_operations_supervisor) {
      this.table.columns.splice(2, 0, 'communication')
    }
    this.fetchItems()
    if (this.is_general_manager || this.is_sales_manager || this.is_sales_supervisor) this.table.columns.push('controls')
  },
  methods: {
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard',
          })
        })
        .catch(() => { console.error('Something went wrong') })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : '-'
    },
    formatAmount(value) {
      return `$${(Number(value) || 0).toLocaleString()}`
    },
    tooltipContent(data) {
      return `${data.safari_insurance_company}<br>${data.safari_insurance_number}<br>${this.dateFormat(data.safari_insurance_coverage_start)} - ${this.dateFormat(data.safari_insurance_coverage_end)}<br>${data.safari_insurance_emergency_phone}`
    },
    onDeleteButtonClick(row) {
      this.current_client = row
      this.removal_reason = ''
      this.deleting = false
      this.$modal.show('delete-detail')
    },
    focusDetailClose() {
      this.$refs.detailHeader.focusClose()
    },
    deleteClient() {
      this.deleting = true
      this.$refs.confirmDelete.preload = true
      return deleteClientSafariGroup(this.$route.params.id, this.current_client.id, this.removal_reason)
        .then(() => {
          this.fetchItems()
          this.$emit('getSafariInfo')
          this.$modal.hide('delete-detail')
          this.removal_reason = ''
          this.$notify({
            type: 'success',
            title: 'Client deleted',
          })
        })
        .finally(() => {
          this.deleting = false
          this.$refs.confirmDelete.preload = false
        })
    },
    getUserAvatar(crop_url) {
      if (crop_url) return crop_url
      return require('@/assets/img/temp/avatar-slug.jpg')
    },
    tooltipSnackBarContent(data) {
      if (!data) return
      if (!data.completed) return 'Not completed'
      if (data.no_need) return 'No need'
      if (data.completed && data.outstanding_amount > 0) return `Completed. Unpaid\nItems: ${data.items_count}\nOrder amount: ${data.order_amount.toLocaleString()}$\nCredit: ${data.credit.toLocaleString()}$\nOutstanding amount: ${data.outstanding_amount.toLocaleString()}$`
      if (data.completed && data.outstanding_amount === 0) return `Completed.\nItems: ${data.items_count}\nOrder amount: ${data.order_amount.toLocaleString()}$\nCredit: ${data.credit.toLocaleString()}$`
    },
    fetchItems() {
      getSafariGroupsClientList(this.$route.params.id)
        .then(res => {
          this.table.items = res.data
        })
    },
  }
}
</script>

<style scoped lang="scss">
.cancel-client-dialog__body {
  padding: $ui-space-24;
}

.cancel-client-dialog__client {
  margin: 0 0 $ui-space-16;
  padding-bottom: $ui-space-16;
  border-bottom: 1px solid $ui-line;

  div {
    display: flex;
    justify-content: space-between;
    gap: $ui-space-12;
  }

  dt {
    color: $ui-muted;
    font-size: $ui-font-size-body;
  }

  dd {
    margin: 0;
    color: $ui-ink;
    font-size: $ui-font-size-body;
    font-weight: 600;
    text-align: right;
  }
}

.cancel-client-dialog__section {
  margin-bottom: $ui-space-24;

  h3 {
    margin: 0 0 $ui-space-12;
    color: $ui-ink;
    font-family: $ui-font-heading;
    font-size: $ui-font-size-section-title;
    font-weight: 600;
  }
}

.cancel-client-dialog__question {
  margin: 0;
  color: $ui-text;
  font-size: $ui-font-size-body;
  line-height: 1.45;
}

.cancel-client-dialog__name {
  margin: $ui-space-8 0 0;
  color: $ui-ink;
  font-size: $ui-font-size-body;
  font-weight: 600;
}

.cancel-client-dialog__facts {
  margin: 0;
  padding: $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: 12px;
  background: $ui-surface-soft;

  div {
    display: flex;
    justify-content: space-between;
    gap: $ui-space-12;

    & + div { margin-top: $ui-space-8; }
  }

  dt {
    margin: 0;
    color: $ui-muted;
    font-size: $ui-font-size-body;
  }

  dd {
    margin: 0;
    color: $ui-ink;
    font-size: $ui-font-size-body;
    font-weight: 600;
    text-align: right;
  }
}

.cancel-client-dialog__refund-note {
  margin: $ui-space-12 0 0;
  padding: $ui-space-12;
  border-radius: 8px;
  background: $ui-surface-soft;
  color: $ui-muted;
  font-size: $ui-font-size-body;
  line-height: 1.45;
}

.cancel-client-dialog__refund-note--blocked {
  background: $ui-red-soft;
  color: $ui-ink;
}
</style>
